import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { getDebtors } from 'src/redux/slices/cheque-factoring';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IDebtor } from 'src/types/cheque-factoring';

import DebtorTableRow from '../debtor-table-row';
import CandidatesTableToolbar from '../candidates-table-toolbar';

const TABLE_HEAD = [
  { id: 'name', label: 'Debtor / Account Number', align: 'left', primary: true },
  { id: 'bank', label: 'Bank', align: 'left', primary: true },
  { id: 'returnCount', label: 'Return History', align: 'left', primary: true },
  { id: 'lastReturnDate', label: 'Last Return', align: 'left', primary: true },
];

export default function DebtorListView() {
  const dispatch = useAppDispatcher();
  const { debtors } = useAppSelector((state) => state.chequeFactoring);

  const settings = useSettingsContext();
  const table = useTable({ defaultRowsPerPage: 25, defaultDense: true });
  const denseHeight = table.dense ? 60 : 80;

  const [generalSearch, setGeneralSearch] = useState('');

  const fetchDebtors = useCallback(() => {
    dispatch(
      getDebtors({
        page: table.page,
        limit: table.rowsPerPage,
        generalSearch,
        sortBy: 'createdAt',
        sort: 'desc',
      })
    );
  }, [dispatch, table.page, table.rowsPerPage, generalSearch]);

  useEffect(() => {
    fetchDebtors();
  }, [fetchDebtors]);

  const handleSearch = useCallback(
    (value: string) => {
      table.onResetPage();
      setGeneralSearch(value);
    },
    [table]
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Debtors"
        links={[{ name: 'Cheque Factoring' }, { name: 'Debtors' }]}
        sx={{ mb: { xs: 3 } }}
      />

      <Card>
        <CandidatesTableToolbar onSearch={handleSearch} />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
              <TableHeadCustom headLabel={TABLE_HEAD} rowCount={debtors.items.length} />

              <TableBody>
                {debtors.status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((_, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {debtors.items.map((row: IDebtor) => (
                      <DebtorTableRow key={row._id} row={row} />
                    ))}
                  </>
                )}

                {debtors.status !== 'failure' && debtors.count === 0 && (
                  <TableNoData message="No debtors found" notFound={debtors.count === 0} />
                )}

                {debtors.status === 'failure' && (
                  <TableErrorData notFound error={debtors.error} />
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense
          count={debtors.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
